import './payment-surfaces.css';
import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocalizedContent } from '../../i18n/localized.ts';
import { paymentApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, ConfirmDialog, Select, TextInput, classes } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { PdfDownloadButton } from '../pdf-download.tsx';
import { StarterNudge } from '../starter-nudge.tsx';
import { formatCents } from '@shared/calculations/pricing.ts';
import { chargeFullyRefunded, quotePaidCents, refundableCentsForCharge } from '@shared/calculations/payments.ts';
import { getDateLocale } from '../../i18n/locale.ts';
import type { QuotePaymentStatus } from '@shared/constants/index.ts';
import type { Payment, Quote } from '@shared/types/index.ts';
import { formatPaymentDay } from '../../i18n/datetime.ts';

/** The query every screen showing a quote's payments shares. */
export function quotePaymentsKey(quoteId: string) {
  return ['quote-payments', quoteId] as const;
}

const STATUS_TONES: Record<QuotePaymentStatus, string> = {
  unpaid: 'border-[color:var(--color-line)] text-[color:var(--color-muted)]',
  partly_paid: 'border-[color:var(--color-warning)] text-[color:var(--color-warning)]',
  paid: 'border-[color:var(--color-brand)] bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]',
};

/** Paid, Partly paid or Unpaid, for an accepted one-time quote. */
export function QuotePaymentBadge({ status }: { status: QuotePaymentStatus }) {
  const { t } = useTranslation();
  return <span className={classes('badge', STATUS_TONES[status])}>{t(`quotePayments.status.${status}`)}</span>;
}

/**
 * What a customer has paid on a quote, and the way to write down what they
 * pay next — on every plan, Free included (0036_quote_payments.sql).
 *
 * Shown for a quote the customer has accepted, and for any quote that already
 * has payments on it, which keeps them in view after the quote has moved on.
 * The status — Paid, Partly paid, Unpaid — is measured against the quote's
 * total only for a one-time job; a recurring or monthly quote is paid visit by
 * visit, so its payments are listed without one.
 *
 * Recording is cash, a check, a transfer, or a type the company added (Zelle,
 * Venmo), and needs no payment account at all. A payment recorded by mistake
 * comes off as a refund, which is also what lets the quote be deleted later.
 */
export function QuotePayments({
  quote,
  recordedOnJob = false,
}: {
  quote: Quote;
  /**
   * The quote is booked as a single job, whose visit takes the payment for
   * the whole balance (the job card's Get paid). What was recorded here
   * before booking stays listed; a new payment goes on the visit.
   */
  recordedOnJob?: boolean;
}) {
  const { t } = useTranslation();
  const localized = useLocalizedContent();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const canRead = can('payments:read');
  const canWrite = can('payments:write');
  const canRefund = can('payments:refund');

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [amount, setAmount] = useState<number | null>(null);
  // The balance the amount was filled in from. Recorded as it stands, it is
  // sent along, so the server refuses it once the quote owes something else
  // (two people recording the same balance, say) instead of taking it twice.
  const [filledFrom, setFilledFrom] = useState<number | null>(null);
  const [methodId, setMethodId] = useState('');
  const [receivedOn, setReceivedOn] = useState('');
  const [note, setNote] = useState('');
  const [addingType, setAddingType] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [refundFor, setRefundFor] = useState<string | null>(null);
  const [refundAmount, setRefundAmount] = useState<number | null>(null);
  const [refundReason, setRefundReason] = useState('');
  // The refund the form asked about. A refund cannot be taken back, so a
  // submitted form (a phone keyboard's Go included) only raises the question,
  // with the amount in it.
  const [confirmingRefund, setConfirmingRefund] = useState<{ payment: Payment; amountCents: number } | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);

  const ledger = useQuery({
    queryKey: quotePaymentsKey(quote.id),
    queryFn: () => paymentApi.quotePayments(quote.id),
    enabled: canRead,
  });
  const methods = useQuery({
    queryKey: ['payment-methods', 'active'],
    queryFn: () => paymentApi.listMethods(),
    enabled: canWrite,
  });

  const money = (cents: number, code = ledger.data?.currencyCode ?? quote.currencyCode) =>
    formatCents(cents, code, getDateLocale());

  function done(message: string) {
    setError(null);
    setNotice(message);
    void queryClient.invalidateQueries({ queryKey: quotePaymentsKey(quote.id) });
    // The customer's own history and what is owed both read these rows, and
    // so do the job card and the Booked list, which count a deposit toward
    // the job: one given back is owed again there too.
    void queryClient.invalidateQueries({ queryKey: ['customer-payments', quote.customerId] });
    void queryClient.invalidateQueries({ queryKey: ['owed'] });
    void queryClient.invalidateQueries({ queryKey: ['single-job', quote.id] });
    void queryClient.invalidateQueries({ queryKey: ['booked-jobs'] });
  }
  const failed = (caught: unknown) =>
    setError(caught instanceof ApiRequestError ? caught.message : t('quotePayments.actionFailed'));

  const record = useMutation({
    mutationFn: () =>
      paymentApi.recordPayment({
        customerId: quote.customerId,
        quoteId: quote.id,
        ...(amount === null ? {} : { amountCents: amount }),
        ...(amount !== null && amount === filledFrom ? { expectedBalanceCents: filledFrom } : {}),
        methodId,
        ...(note.trim() ? { description: note.trim() } : {}),
        ...(receivedOn ? { receivedOn } : {}),
      }),
    onSuccess: () => {
      setRecording(false);
      setAmount(null);
      setFilledFrom(null);
      setNote('');
      setReceivedOn('');
      done(t('quotePayments.recorded'));
    },
    onError: (caught: unknown) => {
      failed(caught);
      // Refused because the quote's payments moved on: the message says what
      // it owes now, so pressing Record again on the same figure is meant,
      // and goes as typed. The ledger is read again for the next form.
      if (caught instanceof ApiRequestError && (caught.status === 400 || caught.status === 409)) setFilledFrom(null);
      void queryClient.invalidateQueries({ queryKey: quotePaymentsKey(quote.id) });
    },
  });
  const addType = useMutation({
    mutationFn: () => paymentApi.createMethod({ name: newTypeName.trim(), family: 'other' }),
    onSuccess: (created) => {
      setAddingType(false);
      setNewTypeName('');
      setMethodId(created.id);
      void queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
    },
    onError: failed,
  });
  const refund = useMutation({
    mutationFn: (input: { paymentId: string; amountCents?: number; reason?: string }) =>
      paymentApi.refund(input.paymentId, {
        ...(input.amountCents === undefined ? {} : { amountCents: input.amountCents }),
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: () => {
      closeRefund();
      done(t('quotePayments.refunded'));
    },
    onError: failed,
  });
  const resendReceipt = useMutation({
    mutationFn: (paymentId: string) => {
      setResendingId(paymentId);
      return paymentApi.resendReceipt(paymentId);
    },
    onSuccess: (result) => {
      setResendingId(null);
      done(t('quotePayments.receiptResent', { email: result.resentTo }));
    },
    onError: (caught: unknown) => {
      setResendingId(null);
      failed(caught);
    },
  });

  if (!canRead || !ledger.data) return null;
  const { items, summary, takesPayments, oneTime, jobBooked } = ledger.data;
  const jobHoldsPayment = ledger.data.jobHoldsPayment === true;
  // Nothing to show on a quote that takes no payments and holds none, or on
  // one whose job card above carries all of its money.
  if (!takesPayments && items.length === 0) return null;
  if (jobBooked && recordedOnJob && items.length === 0) return null;
  // Once its job is booked or done, what is owed is the job's: its card on
  // Free, its visit's invoice on a paid plan. Money paid on the quote before
  // the booking counts toward that, so the quote's own Paid, Partly paid or
  // Unpaid against its whole total would contradict the job's.
  const shownSummary = jobBooked ? null : summary;
  const paidBeforeBooking = jobBooked ? quotePaidCents(items) : 0;

  const activeMethods = methods.data?.items ?? [];

  function openRecord() {
    setError(null);
    setNotice(null);
    // The rest of what is owed is the usual payment; a part of it is a matter
    // of editing the figure down.
    const balance = summary && summary.balanceCents > 0 ? summary.balanceCents : null;
    setAmount(balance);
    setFilledFrom(balance);
    setRecording(true);
  }

  function submitRecord(event: FormEvent) {
    event.preventDefault();
    if (!methodId || amount === null || amount <= 0) return;
    record.mutate();
  }

  function refundableOn(paymentId: string): number {
    return refundableCentsForCharge(paymentId, items);
  }

  function openRefund(payment: Payment) {
    setError(null);
    setNotice(null);
    setRefundFor(payment.id);
    setRefundAmount(refundableOn(payment.id));
    setRefundReason('');
  }

  function closeRefund() {
    setRefundFor(null);
    setRefundAmount(null);
    setRefundReason('');
  }

  function submitRefund(event: FormEvent, payment: Payment) {
    event.preventDefault();
    const remaining = refundableOn(payment.id);
    if (refundAmount === null || refundAmount <= 0 || refundAmount > remaining) {
      setError(t('quotePayments.refundInvalid', { amount: money(remaining, payment.currencyCode) }));
      return;
    }
    setConfirmingRefund({ payment, amountCents: refundAmount });
  }

  function issueRefund(payment: Payment, amountCents: number) {
    refund.mutate({
      paymentId: payment.id,
      // Always the figure the question named: a missing amount is the server's
      // whole cap, which may have grown since this page read it.
      amountCents,
      ...(refundReason.trim() ? { reason: refundReason.trim() } : {}),
    });
  }

  function rowLabel(payment: Payment): string {
    if (payment.type === 'refund') return t('quotePayments.refundLabel');
    return payment.manualMethodName ?? t('quotePayments.recordedLabel');
  }

  return (
    <section className="card work-section payment-leaf mb-6" aria-labelledby="quote-payments-heading">
      <div className="work-section-heading">
        <h2 id="quote-payments-heading" className="text-lg font-semibold">
          {t('quotePayments.heading')}
        </h2>
        {shownSummary ? <QuotePaymentBadge status={shownSummary.status} /> : null}
      </div>

      {shownSummary ? (
        <p className="mb-3 text-sm">
          {shownSummary.status === 'paid'
            ? t('quotePayments.summaryPaid', {
                paid: money(shownSummary.paidCents),
                total: money(ledger.data.totalCents),
              })
            : t('quotePayments.summary', {
                paid: money(shownSummary.paidCents),
                total: money(ledger.data.totalCents),
                balance: money(shownSummary.balanceCents),
              })}
        </p>
      ) : jobBooked ? (
        paidBeforeBooking > 0 ? (
          <p className="mb-3 text-sm">{t('quotePayments.paidBeforeBooking', { paid: money(paidBeforeBooking) })}</p>
        ) : null
      ) : !oneTime ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('quotePayments.paidByVisit')}</p>
      ) : null}

      {/* The quote moved on (declined, canceled, back to a draft) with money
          already on it: the money stays, and says so. */}
      {!takesPayments && items.length > 0 ? (
        <div className="mb-3">
          <Alert tone="warning">{t('quotePayments.movedOn')}</Alert>
        </div>
      ) : null}

      {error ? (
        <div className="mb-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}
      {notice ? (
        <div className="mb-3">
          <Alert tone="success">{notice}</Alert>
        </div>
      ) : null}

      {canWrite && takesPayments && !recording && !recordedOnJob && !jobBooked && !jobHoldsPayment ? (
        <div className="mb-3">
          <Button variant="primary" onClick={openRecord}>
            {t('quotePayments.record')}
          </Button>
        </div>
      ) : null}
      {/* Booked where no job card stands in for it: say where its payment goes. */}
      {canWrite && takesPayments && jobBooked && !recordedOnJob ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('quotePayments.bookedOnVisit')}</p>
      ) : null}
      {/* A canceled booking that was paid: the refund comes first, on the job. */}
      {canWrite && takesPayments && !jobBooked && jobHoldsPayment && !recordedOnJob ? (
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('quotePayments.heldByCanceledJob')}</p>
      ) : null}

      {recording && canWrite ? (
        <form
          onSubmit={submitRecord}
          className="mb-3 grid gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-2"
        >
          <div>
            <Select
              label={t('quotePayments.typeLabel')}
              value={methodId}
              onChange={(event) => setMethodId(event.target.value)}
              options={[
                { value: '', label: t('quotePayments.typePlaceholder') },
                ...activeMethods.map((method) => ({
                  value: method.id,
                  label: localized.text(method.name, method.nameTranslations),
                })),
              ]}
            />
            {addingType ? (
              <div className="flex flex-wrap items-end gap-2">
                <TextInput
                  label={t('quotePayments.newTypeLabel')}
                  value={newTypeName}
                  placeholder={t('quotePayments.newTypePlaceholder')}
                  maxLength={40}
                  onChange={(event) => setNewTypeName(event.target.value)}
                />
                <Button
                  busy={addType.isPending}
                  disabled={!newTypeName.trim()}
                  onClick={() => addType.mutate()}
                  className="mb-4"
                >
                  {t('quotePayments.addTypeSubmit')}
                </Button>
              </div>
            ) : (
              <button
                type="button"
                className="text-sm text-[color:var(--color-brand)] underline"
                onClick={() => setAddingType(true)}
              >
                {t('quotePayments.addType')}
              </button>
            )}
          </div>
          <MoneyInput
            label={t('quotePayments.amountLabel')}
            valueCents={amount}
            onChangeCents={setAmount}
            currencyCode={ledger.data.currencyCode}
          />
          <TextInput
            label={t('quotePayments.dateLabel')}
            type="date"
            value={receivedOn}
            hint={t('quotePayments.dateHint')}
            onChange={(event) => setReceivedOn(event.target.value)}
          />
          <TextInput
            label={t('quotePayments.noteLabel')}
            value={note}
            placeholder={t('quotePayments.notePlaceholder')}
            maxLength={200}
            onChange={(event) => setNote(event.target.value)}
          />
          <p className="text-sm text-[color:var(--color-muted)] sm:col-span-2">{t('quotePayments.receiptHint')}</p>
          <div className="flex flex-wrap gap-2 sm:col-span-2">
            <Button
              type="submit"
              variant="primary"
              busy={record.isPending}
              disabled={!methodId || amount === null || amount <= 0}
            >
              {t('quotePayments.submit')}
            </Button>
            <Button variant="ghost" onClick={() => setRecording(false)}>
              {t('quotePayments.cancel')}
            </Button>
          </div>
          <StarterNudge id="quote-payments" message={t('quotePayments.starterNudge')} className="sm:col-span-2" />
        </form>
      ) : null}

      {items.length > 0 ? (
        <ul className="divide-y divide-[color:var(--color-line)] text-sm">
          {items.map((payment) => {
            const remaining =
              canRefund && payment.type === 'charge' && payment.status === 'succeeded' ? refundableOn(payment.id) : 0;
            const open = refundFor === payment.id;
            return (
              <li key={payment.id} className="py-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    <span className="tabular font-medium">
                      {payment.type === 'refund' ? '−' : ''}
                      {money(payment.amountCents, payment.currencyCode)}
                    </span>
                    <span className="ml-2 text-[color:var(--color-muted)]">
                      {rowLabel(payment)}
                      {` · ${formatPaymentDay(payment)}`}
                      {payment.description ? ` · ${payment.description}` : ''}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-center gap-2">
                    {payment.type === 'charge' && payment.status === 'succeeded' ? (
                      <>
                        <PdfDownloadButton url={paymentApi.receiptUrl(payment.id)} variant="link">
                          {t('quotePayments.downloadReceipt')}
                        </PdfDownloadButton>
                        {canWrite ? (
                          <Button
                            variant="ghost"
                            busy={resendingId === payment.id}
                            onClick={() => resendReceipt.mutate(payment.id)}
                          >
                            {t('quotePayments.resendReceipt')}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {remaining > 0 && !open ? (
                      <Button variant="ghost" onClick={() => openRefund(payment)}>
                        {t('quotePayments.refund')}
                      </Button>
                    ) : payment.type === 'charge' &&
                      payment.status === 'succeeded' &&
                      canRefund &&
                      chargeFullyRefunded(payment.id, items) ? (
                      <span className="text-[color:var(--color-muted)]">{t('quotePayments.fullyRefunded')}</span>
                    ) : null}
                  </span>
                </div>
                {open ? (
                  <form
                    onSubmit={(event) => submitRefund(event, payment)}
                    className="mt-2 grid gap-2 rounded-md border border-[color:var(--color-line)] p-3 sm:grid-cols-2"
                  >
                    <MoneyInput
                      label={t('quotePayments.refundAmountLabel')}
                      hint={t('quotePayments.refundAmountHint', { amount: money(remaining, payment.currencyCode) })}
                      valueCents={refundAmount}
                      onChangeCents={setRefundAmount}
                      currencyCode={payment.currencyCode}
                    />
                    <TextInput
                      label={t('quotePayments.refundReasonLabel')}
                      value={refundReason}
                      placeholder={t('quotePayments.refundReasonPlaceholder')}
                      maxLength={200}
                      onChange={(event) => setRefundReason(event.target.value)}
                    />
                    <div className="flex flex-wrap gap-2 sm:col-span-2">
                      <Button
                        type="submit"
                        variant="primary"
                        busy={refund.isPending}
                        disabled={refundAmount === null || refundAmount <= 0 || refundAmount > remaining}
                      >
                        {t('quotePayments.refundSubmit')}
                      </Button>
                      <Button variant="ghost" onClick={closeRefund}>
                        {t('quotePayments.cancel')}
                      </Button>
                    </div>
                  </form>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-[color:var(--color-muted)]">{t('quotePayments.empty')}</p>
      )}
      {confirmingRefund ? (
        <ConfirmDialog
          title={t(
            confirmingRefund.payment.method === 'manual'
              ? 'singleJob.confirmRecordedRefund'
              : 'singleJob.confirmRefund',
            {
              amount: money(confirmingRefund.amountCents, confirmingRefund.payment.currencyCode),
            },
          )}
          confirmLabel={t('quotePayments.refundSubmit')}
          initialFocus="cancel"
          onConfirm={() => {
            setConfirmingRefund(null);
            issueRefund(confirmingRefund.payment, confirmingRefund.amountCents);
          }}
          onCancel={() => setConfirmingRefund(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * "Already paid on this quote: $X", for the screens that book a quote into
 * visits. Payments recorded on a quote stay on it after booking — they are
 * never moved onto a visit — so the office sees them here and can set the
 * visit's price with them in mind. Says nothing when nothing was paid.
 */
export function AlreadyPaidOnQuote({ quoteId, className }: { quoteId: string; className?: string }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const ledger = useQuery({
    queryKey: quotePaymentsKey(quoteId),
    queryFn: () => paymentApi.quotePayments(quoteId),
    enabled: can('payments:read'),
  });
  if (!ledger.data) return null;
  // The summary's own figure where the quote has one, the same arithmetic
  // where it does not (a recurring quote's deposit is still money taken).
  const paid = ledger.data.summary?.paidCents ?? quotePaidCents(ledger.data.items);
  if (paid <= 0) return null;
  return (
    <p className={classes('text-sm font-semibold text-[color:var(--color-warning)]', className)}>
      {t('quotePayments.alreadyPaid', { amount: formatCents(paid, ledger.data.currencyCode, getDateLocale()) })}
    </p>
  );
}
